// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Appointment, ChargeItem, Coding, Patient } from '@medplum/fhirtypes';
import { isInsuredCopayPatient, isSelfPayPatient } from './extensions';

/** Coding system for Polaris charge-item definition codes (used for the bundle's non-CPT code). */
export const SELF_PAY_BUNDLE_CODE_SYSTEM = 'http://polarissleep.com/charge-item-definitions';

/** Code identifying the self-pay bundle ChargeItem / ChargeItemDefinition. */
export const SELF_PAY_BUNDLE_CODE = 'self-pay-bundle';

/** Canonical URL of the $400 self-pay bundle ChargeItemDefinition. */
export const SELF_PAY_BUNDLE_CHARGE_ITEM_DEFINITION_URL =
  'http://polarissleep.com/charge-item-definitions/self-pay-bundle';

/** Human-readable display for the bundle. */
export const SELF_PAY_BUNDLE_DISPLAY = 'Self-Pay Bundle (Intake + HSAT + Results)';

/** Provenance tag system for ChargeItems. */
export const CHARGE_ITEM_SOURCE_SYSTEM = 'http://polarissleep.com/charge-item-source';

/** Provenance tag code marking a self-pay ChargeItem (per SELFPAY_SPEC R-7.2). */
export const CHARGE_ITEM_SOURCE_SELF_PAY = 'selfpay';

/** Provenance tag code marking an insured-copay-pilot bundle ChargeItem. */
export const CHARGE_ITEM_SOURCE_INSURED_COPAY = 'insured-copay';

/** Flat copay (in integer cents) an insured-copay-pilot patient pays for the legacy bundle. */
export const INSURED_COPAY_AMOUNT_CENTS = 5000;

/** Synthetic promotion code recorded on the insured-copay bundle charge (no real Stripe coupon). */
export const INSURED_COPAY_PROMO_CODE = 'INSURED-COPAY-PILOT';

/** Appointment type codes whose pre-encounter charge is covered by a self-pay bundle. */
export const SELF_PAY_BUNDLE_COVERED_APPOINTMENT_CODES = [
  'new-patient-intake',
  'new-patient-results',
  'existing-patient-results',
] as const;

/** True when the patient has a `billed` self-pay bundle ChargeItem. */
export async function hasBilledSelfPayBundle(medplum: MedplumClient, patientId: string): Promise<boolean> {
  if (!patientId) {
    return false;
  }
  const matches = (await medplum.searchResources('ChargeItem', { subject: `Patient/${patientId}` })) as ChargeItem[];
  return matches.some(
    (ci) =>
      ci.status === 'billed' &&
      (ci.code?.coding ?? []).some(
        (c: Coding) => c.system === SELF_PAY_BUNDLE_CODE_SYSTEM && c.code === SELF_PAY_BUNDLE_CODE
      )
  );
}

/** True when a self-pay bundle has been recorded, whether payment is deferred or captured. */
export async function hasActiveSelfPayBundle(medplum: MedplumClient, patientId: string): Promise<boolean> {
  if (!patientId) {
    return false;
  }
  const matches = (await medplum.searchResources('ChargeItem', { subject: `Patient/${patientId}` })) as ChargeItem[];
  return matches.some(
    (ci) =>
      (ci.status === 'billable' || ci.status === 'billed') &&
      (ci.code?.coding ?? []).some(
        (c: Coding) => c.system === SELF_PAY_BUNDLE_CODE_SYSTEM && c.code === SELF_PAY_BUNDLE_CODE
      )
  );
}

/** A patient on a bundle flow: self-pay OR insured-copay pilot. */
function isBundlePatient(patient: Patient | undefined): boolean {
  return isSelfPayPatient(patient) || isInsuredCopayPatient(patient);
}

/**
 * Whether the pre-encounter charge for an appointment should be suppressed because a bundle-flow
 * patient's bundle covers it. Applies to self-pay AND insured-copay-pilot patients.
 * Keyed on appointment **type code**, never CPT.
 *
 * - `new-patient-intake`: always suppressed (intake precedes the bundle charge at HSAT-order time).
 * - the legacy HSAT results appointment type is suppressed once the bundle has been recorded.
 * - canonical `follow-up` remains billable because it is also used for treatment follow-ups.
 */
export async function isBundleCoveredAppointment(
  medplum: MedplumClient,
  patient: Patient | undefined,
  appointmentTypeCode: string,
  options?: { appointmentStart?: string; excludeAppointmentId?: string }
): Promise<boolean> {
  if (!isBundlePatient(patient)) {
    return false;
  }
  if (appointmentTypeCode === 'new-patient-intake') {
    return true;
  }
  if ((SELF_PAY_BUNDLE_COVERED_APPOINTMENT_CODES as readonly string[]).includes(appointmentTypeCode)) {
    return appointmentTypeCode === 'new-patient-intake' || hasActiveSelfPayBundle(medplum, patient?.id ?? '');
  }
  // Legacy results types are normalized to `follow-up`, which is also used for treatment.
  // Cover exactly the first follow-up after the bundle, not subsequent treatment visits.
  if (appointmentTypeCode !== 'follow-up' || !options?.appointmentStart) {
    return false;
  }
  const bundles = (await medplum.searchResources('ChargeItem', {
    subject: `Patient/${patient?.id ?? ''}`,
  })) as ChargeItem[];
  const activeBundles = bundles.filter(
    (item) =>
      (item.status === 'billable' || item.status === 'billed') &&
      (item.code?.coding ?? []).some(
        (coding) => coding.system === SELF_PAY_BUNDLE_CODE_SYSTEM && coding.code === SELF_PAY_BUNDLE_CODE
      )
  );
  const bundleDate = activeBundles
    .map((item) => item.occurrenceDateTime ?? item.meta?.lastUpdated)
    .filter((date): date is string => Boolean(date))
    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];
  const appointmentTime = new Date(options.appointmentStart).getTime();
  if (!bundleDate || !Number.isFinite(appointmentTime) || appointmentTime < new Date(bundleDate).getTime()) {
    return false;
  }
  const appointments = (await medplum.searchResources('Appointment', {
    patient: `Patient/${patient?.id ?? ''}`,
    date: `ge${bundleDate}`,
    _count: '1000',
  })) as Appointment[];
  const consumingStatuses = new Set(['booked', 'pending', 'arrived', 'checked-in', 'fulfilled']);
  return !appointments.some((appointment) => {
    if (
      appointment.id === options.excludeAppointmentId ||
      !consumingStatuses.has(appointment.status ?? '') ||
      !appointment.appointmentType?.coding?.some((coding) => coding.code === 'follow-up') ||
      !appointment.start
    ) {
      return false;
    }
    const priorTime = new Date(appointment.start).getTime();
    return Number.isFinite(priorTime) && priorTime >= new Date(bundleDate).getTime() && priorTime < appointmentTime;
  });
}

/** @deprecated renamed to {@link isBundleCoveredAppointment} (now covers insured-copay too). */
export const isSelfPayBundleCoveredAppointment = isBundleCoveredAppointment;
